import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useVersionCheck } from "../hooks/useVersionCheck";

/**
 * UpdateBanner — barra flotante que avisa cuando hay una versión nueva del
 * sistema desplegada, para que el usuario no se quede en una pestaña vieja
 * sin saberlo. No es intrusiva: no bloquea la pantalla, se puede posponer.
 */
/**
 * Recarga "de verdad": antes de refrescar, borra el caché del service worker y
 * fuerza a que tome el control la versión nueva. Sin esto, en un dispositivo
 * con la app instalada el navegador podía seguir sirviendo el build viejo
 * desde el caché y la recarga no cambiaba nada.
 */
async function recargarLimpio() {
  try {
    if ("caches" in window) {
      const claves = await caches.keys();
      await Promise.all(claves.map((c) => caches.delete(c)));
    }
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await reg.update();
      reg.waiting?.postMessage({ type: "SKIP_WAITING" });
    }
  } catch { /* si algo falla igual recargamos: peor es quedarse en la versión vieja */ }
  // reload(true) está deprecado y no hace nada en navegadores modernos; pedir
  // la URL con un parámetro nuevo sí evita cualquier copia cacheada del HTML.
  const url = new URL(window.location.href);
  url.searchParams.set("_v", Date.now().toString());
  window.location.replace(url.toString());
}

export default function UpdateBanner() {
  const { t } = useTranslation();
  const updateAvailable = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);

  if (!updateAvailable || dismissed) return null;

  return (
    <div style={{
      position: "fixed", left: "50%", bottom: 20, transform: "translateX(-50%)",
      zIndex: 4000, display: "flex", alignItems: "center", gap: 12,
      background: "var(--bg-card)", border: "1px solid rgba(var(--brand-rgb), 0.4)",
      borderRadius: 12, padding: "12px 16px",
      boxShadow: "0 12px 36px rgba(0,0,0,0.35)",
      maxWidth: "92vw",
    }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0,
        background: "rgba(var(--brand-rgb), 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className="fa-solid fa-rotate" style={{ color: "var(--brand)", fontSize: "0.95rem" }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--text-primary)" }}>
          {t("updateBanner.titulo")}
        </div>
        <div style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>
          {t("updateBanner.subtitulo")}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 6 }}>
        <button type="button" onClick={() => setDismissed(true)}
          style={{ background: "transparent", border: "1px solid var(--border-card)",
            borderRadius: 8, color: "var(--text-muted)", cursor: "pointer",
            padding: "7px 12px", fontSize: "0.8rem", fontWeight: 600 }}>
          {t("updateBanner.despues")}
        </button>
        <button type="button" onClick={recargarLimpio}
          style={{ background: "var(--brand)", border: "none", borderRadius: 8,
            color: "#fff", cursor: "pointer", padding: "7px 14px",
            fontSize: "0.8rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
          <i className="fa-solid fa-arrows-rotate" />{t("updateBanner.actualizar")}
        </button>
      </div>
    </div>
  );
}
